// Shared building blocks for the Platform, Integrations and Pricing pages.
// Typographic, hairline-led, no icon grids. Copy comes from content.ts or the page data files.
import React, { useEffect, useState } from 'react';
import { cta, booking, industries, type Status } from '../../content';
import { Arrow, Button, SmartLink, StatusChip, Swipe, cx, useReveal } from '../../ui';
import { BookingWidget } from '../../booking/BookingWidget';

/* ---------- head helpers ---------- */

// Injects a JSON-LD block into <head> while the page is mounted.
export function useJsonLd(id: string, data: object | null) {
  const json = data ? JSON.stringify(data) : '';
  useEffect(() => {
    if (!json) return;
    const el = document.createElement('script');
    el.type = 'application/ld+json';
    el.id = `ld-${id}`;
    el.text = json;
    document.head.appendChild(el);
    return () => el.remove();
  }, [id, json]);
}

export const faqJsonLd = (faqs: { q: string; a: string }[]) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
});

export const breadcrumbJsonLd = (items: { name: string; path: string }[]) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((it, i) => ({ '@type': 'ListItem', position: i + 1, name: it.name, item: `https://www.brandedmark.com${it.path}` })),
});

/* ---------- type ---------- */

// Headline with the swipe on one short phrase: [before, swiped, after]
export function SwipeLine({ parts, draw }: { parts: [string, string, string?]; draw?: boolean }) {
  const [a, b, c] = parts;
  return (
    <>
      {a}
      {a && ' '}
      <Swipe draw={draw}>{b}</Swipe>
      {c ? ` ${c}` : ''}
    </>
  );
}

// Attio/Ramp-style two-tone heading: indigo statement, muted continuation.
export function TwoTone({ lead, rest, as: Tag = 'h2', className }: { lead: string; rest?: string; as?: 'h2' | 'h3'; className?: string }) {
  return (
    <Tag className={cx(Tag === 'h2' ? 't-h2 pk-tt' : 't-h3', className)}>
      {lead}
      {rest && <span className="pk-muted"> {rest}</span>}
    </Tag>
  );
}

export function Kicker({ children, bar = true, className }: { children: React.ReactNode; bar?: boolean; className?: string }) {
  return (
    <div className={className}>
      {bar && <span className="bar" />}
      <div className="t-eyebrow">{children}</div>
    </div>
  );
}

/* ---------- hero ---------- */

export function PageHero({
  kicker,
  title,
  lede,
  status: _status,
  children,
  aside,
  crumbs,
}: {
  kicker?: React.ReactNode;
  title: React.ReactNode;
  lede: React.ReactNode;
  status?: Status;
  children?: React.ReactNode;
  aside?: React.ReactNode;
  crumbs?: { label: string; href: string }[];
}) {
  return (
    <section className="pk-hero">
      <div className="wrap">
        {crumbs && (
          <nav aria-label="Breadcrumb" className="mb-8 flex flex-wrap items-center gap-2 text-[13.5px] text-bm-muted">
            {crumbs.map((c, i) => (
              <React.Fragment key={c.href}>
                {i > 0 && <span aria-hidden="true">/</span>}
                <SmartLink href={c.href} className="hover:text-bm-indigo">
                  {c.label}
                </SmartLink>
              </React.Fragment>
            ))}
          </nav>
        )}
        <div className={cx('grid items-center gap-12', !!aside && 'lg:grid-cols-[1.08fr_1fr] lg:gap-16')}>
          <div>
            {/* the breadcrumb already names the area, so the kicker shows only without one */}
            {!crumbs && kicker && (
              <div className="mb-5 flex flex-wrap items-center gap-3">
                <Kicker>{kicker}</Kicker>
              </div>
            )}
            <h1 className="pk-h1">{title}</h1>
            <p className="t-lede mt-6 max-w-[34rem]">{lede}</p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              {children ?? (
                <>
                  <Button href={cta.primaryHref}>
                    {cta.primary} <Arrow />
                  </Button>
                  <SmartLink href={cta.secondaryHref} className="pk-textlink text-[15px] sm:ml-2">
                    or {cta.secondary.toLowerCase()}
                  </SmartLink>
                </>
              )}
            </div>
          </div>
          {aside && <div className="min-w-0">{aside}</div>}
        </div>
      </div>
    </section>
  );
}

/* ---------- section scaffolding ---------- */

export function Section({ id, alt, dark, className, children }: { id?: string; alt?: boolean; dark?: boolean; className?: string; children: React.ReactNode }) {
  const ref = useReveal<HTMLElement>();
  return (
    <section
      id={id}
      ref={ref}
      className={cx('section', dark && 'on-dark', className)}
      style={{ background: dark ? 'var(--bm-indigo)' : alt ? 'var(--bm-paper-alt)' : undefined, scrollMarginTop: 80 }}
    >
      <div className="wrap">{children}</div>
    </section>
  );
}

// A row link with a big title, used for "go deeper" lists.
export function RowLink({ href, title, line, status, index }: { href: string; title: string; line: string; status?: Status; index?: string }) {
  return (
    <SmartLink href={href} className="pk-rowlink group">
      <span className="pk-rowlink-n font-mono">{index ?? ''}</span>
      <span className="pk-rowlink-t">{title}</span>
      <span className="pk-rowlink-l">{line}</span>
      <span className="pk-rowlink-s">{status && <StatusChip status={status} />}</span>
      <span className="pk-rowlink-a" aria-hidden="true">
        <Arrow />
      </span>
    </SmartLink>
  );
}

/* ---------- FAQ ---------- */

export function Faq({ faqs, title = 'Questions we get asked', id = 'faq' }: { faqs: { q: string; a: string }[]; title?: string; id?: string }) {
  useJsonLd(`faq-${id}`, faqs.length ? faqJsonLd(faqs) : null);
  const [open, setOpen] = useState<number | null>(0);
  return (
    <div className="grid gap-10 lg:grid-cols-[0.8fr_1.2fr] lg:gap-16" id={id}>
      <div>
        <h2 className="t-h2">{title}</h2>
        <p className="mt-5 max-w-sm text-[16px] text-bm-muted">
          Ask anything else on the call.
        </p>
      </div>
      <div className="border-t border-bm-line">
        {faqs.map((f, i) => {
          const isOpen = open === i;
          return (
            <div key={f.q} className="border-b border-bm-line">
              <h3>
                <button
                  type="button"
                  className="flex w-full items-start justify-between gap-6 py-5 text-left text-[17px] font-semibold text-bm-indigo"
                  aria-expanded={isOpen}
                  onClick={() => setOpen(isOpen ? null : i)}
                >
                  <span>{f.q}</span>
                  <span className={cx('pk-plus mt-1', isOpen && 'is-open')} aria-hidden="true" />
                </button>
              </h3>
              <div className="pk-faq-a" data-open={isOpen}>
                <div>
                  <p className="pb-6 pr-8 text-[16px] leading-relaxed text-bm-ink/80">{f.a}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ---------- related industries ---------- */

// Editorial photography per sector (brand library, 20 Oct). Falls back to the industry's own image.
const SECTOR_PHOTO: Record<string, string> = {
  'agricultural-machinery': '/images/v2/gen/agri-workshop-b-thumb.jpg',
  'lifting-access-equipment': '/images/v2/gen/lifting-inspection-thumb.jpg',
  'heating-heat-pumps': '/images/v2/gen/heatpump-home-thumb.jpg',
  'commercial-kitchen': '/images/v2/gen/kitchen-service-b-thumb.jpg',
  'industrial-precision': '/images/v2/gen/precision-dispatch-thumb.jpg',
  'premium-durables': '/images/v2/gen/hifi-owner-thumb.jpg',
  'sports-fitness-equipment': '/images/v2/gen/fitness-service-thumb.jpg',
};

export function RelatedIndustries({ slugs, title = 'Where this matters most' }: { slugs: string[]; title?: string }) {
  const list = slugs.map((s) => industries.find((i) => i.slug === s)).filter(Boolean) as typeof industries;
  if (!list.length) return null;
  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <h2 className="t-h3">{title}</h2>
        <SmartLink href="/industries" className="pk-textlink">
          All industries <Arrow />
        </SmartLink>
      </div>
      <div className="mt-6 grid gap-px overflow-hidden rounded-[18px] border border-bm-line bg-bm-line sm:grid-cols-3">
        {list.map((i) => (
          <SmartLink key={i.slug} href={`/industries/${i.slug}`} className="group flex flex-col bg-white p-5 transition-colors hover:bg-bm-alt">
            <div className="overflow-hidden rounded-xl">
              <img src={SECTOR_PHOTO[i.slug] ?? i.image} alt="" width={640} height={427} loading="lazy" className="aspect-[16/9] w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]" />
            </div>
            <div className="mt-4 text-[17px] font-semibold text-bm-indigo">{i.name}</div>
            <div className="mt-1 text-[14.5px] text-bm-muted">{i.short}</div>
          </SmartLink>
        ))}
      </div>
    </div>
  );
}

/* ---------- closing CTA ---------- */

export function CtaBand({ source, title, lede }: { source: string; title?: [string, string]; lede?: string }) {
  const [a, b] = title ?? ['Let’s', 'talk.'];
  const ref = useReveal<HTMLElement>();
  return (
    <section ref={ref} className="on-dark" style={{ background: 'var(--bm-indigo)' }}>
      <div className="wrap grid gap-12 py-[clamp(72px,9vw,120px)] lg:grid-cols-[1.1fr_0.9fr] lg:items-center lg:gap-20">
        <div className="min-w-0" data-reveal>
          <h2 className="t-h2">
            {a} <Swipe>{b}</Swipe>
          </h2>
          <p className="t-lede mt-6 max-w-xl">{lede ?? booking.lede}</p>
          <ol className="mt-8 space-y-3">
            {booking.expect.map((e, i) => (
              <li key={e} className="flex gap-4 text-[16px] text-white/85">
                <span className="font-mono text-[13px] leading-[1.9] text-bm-lime">0{i + 1}</span>
                {e}
              </li>
            ))}
          </ol>
        </div>
        <div className="min-w-0" data-reveal style={{ ['--reveal-delay' as string]: '0.1s' }}>
          <BookingWidget compact source={source} />
          <div className="mt-5 flex flex-wrap gap-x-6 gap-y-2 text-[14.5px]">
            <SmartLink href={cta.secondaryHref} className="pk-textlink">
              or {cta.secondary.toLowerCase()} <Arrow />
            </SmartLink>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- styles (scoped by .pk- prefix) ---------- */

export function KitStyles() {
  return (
    <style>{`
.v2 .pk-muted { color: var(--bm-muted); }
.v2 .on-dark .pk-muted { color: rgba(255,255,255,.55); }
.v2 .pk-hero { padding-top: clamp(40px, 6vw, 88px); padding-bottom: clamp(56px, 7vw, 104px); }
.v2 .pk-h1 {
  font-weight: 800; color: var(--bm-indigo);
  font-size: clamp(38px, 5vw, 68px); line-height: 1.02; letter-spacing: -0.04em;
  text-wrap: balance;
}
.v2 .pd .pk-h1 { font-size: clamp(36px, 4.3vw, 58px); }
.v2 .pk-tt { font-size: clamp(30px, 3.4vw, 46px); line-height: 1.06; }
.v2 :is(.pf,.pd,.it,.pr) .chip { white-space: nowrap; flex: none; }
.v2 .t-h2 { text-wrap: balance; }
.v2 .pk-textlink { display: inline-flex; align-items: center; gap: 6px; font-weight: 600; color: var(--bm-indigo); }
.v2 .pk-textlink:hover { color: var(--bm-emerald); }
.v2 .on-dark .pk-textlink { color: #fff; }
.v2 .pk-textlink svg { transition: transform .18s var(--bm-ease); }
.v2 .pk-textlink:hover svg { transform: translateX(3px); }

.v2 .pk-rowlink {
  display: grid; grid-template-columns: 1fr auto; gap: 4px 16px; align-items: center;
  padding: 22px 4px; border-bottom: 1px solid var(--bm-line); color: var(--bm-indigo);
  transition: background-color .18s var(--bm-ease), padding .18s var(--bm-ease);
}
.v2 .pk-rowlink-n { display: none; }
.v2 .pk-rowlink-t { font-weight: 700; font-size: clamp(20px, 2.2vw, 28px); letter-spacing: -0.025em; line-height: 1.15; }
.v2 .pk-rowlink-l { grid-column: 1; color: var(--bm-muted); font-size: 15.5px; }
.v2 .pk-rowlink-s { display: none; }
.v2 .pk-rowlink-a { grid-row: 1 / span 2; grid-column: 2; width: 40px; height: 40px; border-radius: 999px; display: grid; place-items: center; box-shadow: inset 0 0 0 1.5px var(--bm-line); transition: all .18s var(--bm-ease); }
.v2 .pk-rowlink:hover .pk-rowlink-a { background: var(--bm-indigo); color: #fff; box-shadow: none; }
@media (min-width: 900px) {
  .v2 .pk-rowlink { grid-template-columns: 56px minmax(0, 1.1fr) minmax(0, 1fr) 110px 40px; gap: 24px; padding: 26px 8px; }
  .v2 .pk-rowlink:hover { background: var(--bm-paper-alt); }
  .v2 .pk-rowlink-n { display: block; color: var(--bm-muted); font-size: 13px; }
  .v2 .pk-rowlink-l { grid-column: auto; }
  .v2 .pk-rowlink-s { display: block; }
  .v2 .pk-rowlink-a { grid-row: auto; grid-column: auto; }
}

.v2 .pk-plus { position: relative; flex: none; width: 14px; height: 14px; }
.v2 .pk-plus::before, .v2 .pk-plus::after { content: ''; position: absolute; left: 0; right: 0; top: 6px; height: 2px; border-radius: 2px; background: var(--bm-indigo); transition: transform .2s var(--bm-ease); }
.v2 .pk-plus::after { transform: rotate(90deg); }
.v2 .pk-plus.is-open::after { transform: rotate(0deg); }
.v2 .pk-faq-a { display: grid; grid-template-rows: 0fr; transition: grid-template-rows .25s var(--bm-ease); }
.v2 .pk-faq-a[data-open="true"] { grid-template-rows: 1fr; }
.v2 .pk-faq-a > div { overflow: hidden; }
.v2 .pk-faq-a[data-open="false"] > div { visibility: hidden; transition: visibility 0s .25s; }

.v2 .pk-quote { position: relative; font-weight: 600; color: var(--bm-indigo); letter-spacing: -0.015em; }
.v2 .pk-card { background: #fff; border-radius: 20px; box-shadow: 0 0 0 1px var(--bm-line), 0 1px 2px rgba(24,19,67,.04), 0 12px 32px -18px rgba(24,19,67,.18); }
.v2 .pk-hair > * + * { border-top: 1px solid var(--bm-line); }
@keyframes pk-rise { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; } }
.v2 .pk-rise { opacity: 0; animation: pk-rise .5s var(--bm-ease) forwards; }
@media (prefers-reduced-motion: reduce) { .v2 .pk-rise { opacity: 1; animation: none; } }
`}</style>
  );
}
